import { useCallback } from "react";
import type { Map as OlMap } from "ol";
import type { Size } from "ol/size";
import { useMap } from "./useMap";

/**
 * 캡쳐할 영역 — 지도 뷰포트 기준 **CSS 픽셀** 좌표.
 *
 * OL 레이어 캔버스는 device 픽셀이지만 `transform` 매트릭스가 CSS 픽셀로 매핑하므로,
 * 합성·크롭은 전부 CSS 픽셀 공간에서 이뤄진다(= `map.getSize()` 와 같은 단위).
 */
export interface MapCaptureRegion {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface MapCaptureOptions {
  /** 잘라낼 영역. 미지정 시 지도 전체를 캡쳐한다. */
  region?: MapCaptureRegion;
  /** @default "image/png" */
  mimeType?: string;
  /** 손실 포맷일 때의 품질(0~1). PNG 에는 영향 없음. */
  quality?: number;
}

/**
 * 레이어 property 키 — `true` 면 캡쳐 결과에서 제외한다.
 *
 * 선택 강조·정점 핸들·인디케이터·영역 미리보기처럼 **지도 콘텐츠가 아니라 조작 UI** 인 레이어를
 * 표시하는 용도다. 이 표시가 없으면 화면 캔버스를 그대로 자르는 1× 결과에만 조작 UI 가 섞여
 * 배율 간 결과가 갈린다.
 *
 * @example new VectorLayer({ properties: { [EXCLUDE_FROM_CAPTURE]: true } })
 */
export const EXCLUDE_FROM_CAPTURE = "excludeFromCapture";

const DEFAULT_MIME_TYPE = "image/png";
/** 빈 캔버스 판정용 샘플 격자 — 전수 스캔은 수천만 픽셀에서 비현실적이라 격자 표본으로 갈음한다. */
const BLANK_SAMPLE_GRID = 8;
/** `transform: matrix(a, b, c, d, e, f)` 파싱 */
const TRANSFORM_MATRIX_PATTERN = /^matrix\(([^(]*)\)$/;

type CanvasMatrix = [number, number, number, number, number, number];

/**
 * 레이어 캔버스가 자기 자신을 지도 좌표계에 놓는 매트릭스.
 *
 * `transform` 이 없으면 style 크기 대비 캔버스 크기 비율(= device pixel ratio 보정)만 적용한다.
 */
function readLayerMatrix(canvas: HTMLCanvasElement): CanvasMatrix {
  const transform = canvas.style.transform;
  const matched = transform ? TRANSFORM_MATRIX_PATTERN.exec(transform) : null;

  if (matched) {
    return matched[1].split(",").map(Number) as CanvasMatrix;
  }
  return [
    parseFloat(canvas.style.width) / canvas.width,
    0,
    0,
    parseFloat(canvas.style.height) / canvas.height,
    0,
    0,
  ];
}

/**
 * 렌더된 레이어 캔버스들을 DOM 순서(= z-order)대로 하나의 캔버스에 합성한다.
 *
 * `region` 이 주어지면 결과 캔버스는 그 크기이며, 각 레이어의 매트릭스 평행이동 성분에서
 * 크롭 오프셋을 빼서 그린다 — 전체 크기 중간 캔버스를 만들지 않으므로 메모리가 출력 크기로 유지된다.
 */
function composeMapCanvas(
  map: OlMap,
  region?: MapCaptureRegion
): HTMLCanvasElement | null {
  const size = map.getSize() as Size | undefined;
  if (!size) return null;

  const width = Math.round(region?.width ?? size[0]);
  const height = Math.round(region?.height ?? size[1]);
  if (width <= 0 || height <= 0) return null;

  const offsetX = region?.x ?? 0;
  const offsetY = region?.y ?? 0;

  const target = document.createElement("canvas");
  target.width = width;
  target.height = height;

  const context = target.getContext("2d");
  if (!context) return null;

  const layerCanvases = map
    .getViewport()
    .querySelectorAll<HTMLCanvasElement>(".ol-layer canvas, canvas.ol-layer");

  let drewAnyLayer = false;

  layerCanvases.forEach((canvas) => {
    if (canvas.width === 0 || canvas.height === 0) return;

    const parent = canvas.parentNode as HTMLElement | null;
    const opacity = parent?.style.opacity || canvas.style.opacity;
    context.globalAlpha = opacity === "" ? 1 : Number(opacity);

    const [a, b, c, d, e, f] = readLayerMatrix(canvas);
    // 목적지 좌표계 평행이동은 매트릭스 왼쪽에 합성되므로 e·f 만 보정하면 된다.
    context.setTransform(a, b, c, d, e - offsetX, f - offsetY);

    const backgroundColor = parent?.style.backgroundColor;
    if (backgroundColor) {
      context.fillStyle = backgroundColor;
      context.fillRect(0, 0, canvas.width, canvas.height);
    }

    context.drawImage(canvas, 0, 0);
    drewAnyLayer = true;
  });

  context.globalAlpha = 1;
  context.setTransform(1, 0, 0, 1, 0, 0);

  return drewAnyLayer ? target : null;
}

/**
 * 캔버스가 비어 있는지 — 격자 표본이 전부 완전 투명이면 비어 있다고 본다.
 *
 * 브라우저는 캔버스 크기·면적 상한을 넘으면 **예외 없이 빈 캔버스**를 반환하므로,
 * 이 판정이 없으면 상한 초과가 조용한 실패(빈 PNG 다운로드)로 끝난다.
 */
function isBlankCanvas(canvas: HTMLCanvasElement): boolean {
  const context = canvas.getContext("2d");
  if (!context) return true;

  const stepX = Math.max(1, Math.floor(canvas.width / BLANK_SAMPLE_GRID));
  const stepY = Math.max(1, Math.floor(canvas.height / BLANK_SAMPLE_GRID));

  for (let y = 0; y < canvas.height; y += stepY) {
    for (let x = 0; x < canvas.width; x += stepX) {
      const alpha = context.getImageData(x, y, 1, 1).data[3];
      if (alpha !== 0) return false;
    }
  }
  return true;
}

/**
 * 조작 UI 레이어를 감춘 상태에서 `compose` 를 실행하고, 끝나면 원래 가시성으로 되돌린다.
 *
 * 훅이 직접 감추는 이유: 호출부가 감춘 뒤 캡쳐하려면 "감춤 → 렌더 완료 → 캡쳐" 순서를 스스로
 * 보장해야 하는데, 이는 매 호출부에서 틀리기 쉬운 순서다. `renderSync()` 로 동기 렌더를 강제해
 * 여기서 한 번만 옳게 처리한다.
 */
function withCaptureUiHidden<T>(map: OlMap, compose: () => T): T {
  const hidden = map
    .getLayers()
    .getArray()
    .filter((layer) => layer.get(EXCLUDE_FROM_CAPTURE) === true && layer.getVisible());

  if (hidden.length === 0) return compose();

  hidden.forEach((layer) => layer.setVisible(false));
  map.renderSync?.();
  try {
    return compose();
  } finally {
    hidden.forEach((layer) => layer.setVisible(true));
    map.renderSync?.();
  }
}

function canvasToBlob(
  canvas: HTMLCanvasElement,
  mimeType: string,
  quality?: number
): Promise<Blob | null> {
  return new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob), mimeType, quality);
  });
}

/**
 * 지도 캔버스를 **명령형으로 한 번** 캡쳐하는 훅 — `useMap()` 컨텍스트 필요.
 *
 * 캡쳐는 지도의 이벤트가 아니라 사용자의 액션이므로, `rendercomplete` 구독이 아니라
 * 필요한 시점에 호출해 결과를 받는 형태다(구 `CaptureMap` 의 구독형 계약을 대체).
 *
 * 결과가 `null` 이면 **캡쳐 실패**이며 호출부는 반드시 사용자에게 알려야 한다 — 무음 실패 금지.
 * `null` 이 되는 경우: 지도 크기 미확정 · 렌더된 레이어 없음 · 출력이 브라우저 캔버스 상한을
 * 넘어 빈 캔버스가 됨 · 타일 오리진 CORS 미응답으로 캔버스가 tainted 됨 · 인코딩 실패.
 *
 * ⚠️ 타일이 CORS 로 로드되지 않으면(`TileLayer` 의 `crossOrigin`) 캔버스가 tainted 되어
 * 인코딩이 `SecurityError` 로 실패한다. 이 훅은 그 경우도 `null` 로 수렴시킨다.
 *
 * @example
 * const capture = useMapCapture();
 * const blob = await capture({ region: { x: 100, y: 50, width: 800, height: 600 } });
 * if (!blob) notice("error", "캡쳐에 실패했습니다");
 */
export function useMapCapture() {
  const map = useMap();

  return useCallback(
    async (options: MapCaptureOptions = {}): Promise<Blob | null> => {
      // 조작 UI 레이어(선택 강조·인디케이터·영역 미리보기)는 결과에 들어가면 안 된다.
      const canvas = withCaptureUiHidden(map, () =>
        composeMapCanvas(map, options.region)
      );
      if (!canvas) return null;

      try {
        if (isBlankCanvas(canvas)) return null;
        return await canvasToBlob(
          canvas,
          options.mimeType ?? DEFAULT_MIME_TYPE,
          options.quality
        );
      } catch {
        // tainted canvas(SecurityError) 등 — 실패를 null 로 수렴시켜 호출부가 한 갈래로 다룬다.
        return null;
      }
    },
    [map]
  );
}
